import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Style, Icon } from 'ol/style';
import { map } from './Map';
import { toLonLat, fromLonLat } from 'ol/proj';

// --- TOAST NOTIFICATION SYSTEM ---
function showToast(message, type = 'info') {
    let container = document.getElementById('geopst-toast-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'geopst-toast-container';
        container.style.cssText = `
            position: fixed;
            bottom: 30px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 10000;
            display: flex;
            flex-direction: column;
            gap: 10px;
            pointer-events: none;
        `;
        document.body.appendChild(container);
    }

    const toast = document.createElement('div');

    // Icon colors based on type (Background stays themed)
    let iconColor = 'var(--text-primary)';
    let icon = 'fa-info-circle';

    if (type === 'success') { iconColor = 'var(--success)'; icon = 'fa-check-circle'; }
    if (type === 'error') { iconColor = 'var(--primary)'; icon = 'fa-exclamation-circle'; }

    toast.innerHTML = `
        <i class="fas ${icon}" style="margin-left: 12px; color: ${iconColor}; font-size: 1.1em;"></i>
        <span>${message}</span>
    `;

    toast.style.cssText = `
        background: var(--bg-primary);
        color: var(--text-primary);
        padding: 12px 24px;
        border-radius: 50px;
        border: 1px solid var(--border-strong);
        box-shadow: var(--shadow-modal);
        font-family: inherit; 
        font-size: 15px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        direction: rtl;
        z-index: 10001;
        white-space: nowrap;
        pointer-events: auto;
    `;

    container.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateY(0)';
    });

    // Animate out
    setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(20px)';
        setTimeout(() => toast.remove(), 300);
    }, 3000);
}

// --- PIN MANAGEMENT ---
function updateSaveButtonUI() {
    if (!currentFeature || !window.featureSheet) return;
    const isSaved = currentFeature.get('isSaved');
    const btn = document.querySelector('button[data-action="save-loc"]');
    if (btn) {
        if (isSaved) {
            btn.innerHTML = '<i class="fas fa-bookmark"></i> <span>محفوظ</span>';
            btn.classList.add('active-saved');
            btn.style.color = 'var(--primary)';
        } else {
            btn.innerHTML = '<i class="far fa-bookmark"></i> <span>حفظ</span>';
            btn.classList.remove('active-saved');
            btn.style.color = '';
        }
    }
}

const pinSource = new VectorSource();
const svgPin = `
    <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg">
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
            <feOffset dx="0" dy="2" result="offsetblur"/>
            <feComponentTransfer>
                <feFuncA type="linear" slope="0.4"/>
            </feComponentTransfer>
            <feMerge>
                <feMergeNode/>
                <feMergeNode in="SourceGraphic"/>
            </feMerge>
        </filter>
        <path d="M16 0C7.163 0 0 7.163 0 16C0 28 16 42 16 42C16 42 32 28 32 16C32 7.163 24.837 0 16 0Z" fill="#c41e3a" filter="url(#shadow)"/>
        <circle cx="16" cy="16" r="5" fill="white"/>
    </svg>`;
const pinIconUri = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgPin)));

const pinLayer = new VectorLayer({
    source: pinSource,
    zIndex: 9999,
    style: new Style({
        image: new Icon({
            anchor: [0.5, 1],
            src: pinIconUri,
            scale: 1,
            imgSize: [32, 42]
        })
    })
});

map.addLayer(pinLayer);

// Track current active feature being viewed
let currentFeature = null;

// Initialize Saved Pins from Server
async function loadSavedPins() {
    try {
        const fetchFn = window.secureFetch || fetch;
        const response = await fetchFn('/api/users/pins');
        if (!response.ok) throw new Error('Failed to fetch pins');
        
        const savedPins = await response.json();
        const projection = map.getView().getProjection();

        // Clear existing features first to avoid duplicates
        pinSource.clear();

        savedPins.forEach(pin => {
            if (pin.latitude && pin.longitude) {
                // Convert stored Lat/Lon back to Map Coordinates
                const coord = fromLonLat([parseFloat(pin.longitude), parseFloat(pin.latitude)], projection);
                const feature = new Feature(new Point(coord));
                feature.set('isSaved', true);
                feature.set('note', pin.note || ''); // Attach note to feature for reference
                pinSource.addFeature(feature);
            }
        });
        
        // After loading from server, check if we need to sync legacy local data
        syncLocalStorageToCloud();

    } catch (e) {
        console.error('Error loading saved pins', e);
        // Fallback to local only during development or network fail if needed
    }
}

/**
 * 🔄 Sync Logic: Moves legacy localStorage data to the cloud on first run
 */
async function syncLocalStorageToCloud() {
    const localPins = JSON.parse(localStorage.getItem('my_saved_pins') || '[]');
    if (localPins.length === 0) return;

    console.log('[Sync] Moving local pins to cloud...');
    const fetchFn = window.secureFetch || fetch;

    for (const pin of localPins) {
        const note = localStorage.getItem(`pin_${pin.lat}_${pin.lon}`) || '';
        try {
            await fetchFn('/api/users/pins', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ latitude: pin.lat, longitude: pin.lon, note: note })
            });
            // Remove individually if successful
            localStorage.removeItem(`pin_${pin.lat}_${pin.lon}`);
        } catch (e) {
            console.error('[Sync] Failed to sync pin:', pin, e);
        }
    }

    // Clear main key after everything attempted
    localStorage.removeItem('my_saved_pins');
    console.log('[Sync] Local storage cleared after sync.');
    
    // Refresh UI
    loadSavedPins();
}

// Load pins on init
loadSavedPins();

// --- INTERACTION HANDLERS ---

// 1. Click to Select or Deselect
map.on('singleclick', (e) => {
    const hit = map.hasFeatureAtPixel(e.pixel, { layerFilter: l => l === pinLayer });

    if (hit) {
        // Select existing pin
        const features = map.getFeaturesAtPixel(e.pixel, { layerFilter: l => l === pinLayer });
        if (features.length > 0) {
            const feature = features[0];
            const coord = feature.getGeometry().getCoordinates();
            placePin(coord, feature);
        }
    } else {
        // Deselect (Close Sheet) if clicking empty map
        // ⚡ تجاهل النقر إذا تم إسقاط دبوس للتو (منع إغلاق النافذة بعد الضغط الطويل)
        if (recentlyDroppedPin) {
            recentlyDroppedPin = false;
            return;
        }
        if (window.featureSheet && window.featureSheet.isVisible() &&
            window.featureSheet.data && window.featureSheet.data.isPin) {
            window.featureSheet.close();

            // ✅ إرجاع حجم الخريطة عند النقر في مكان فارغ (إذا كانت أداة الاستعلام غير مفعلة)
            if (window.ensureSidebarHidden && !window.GeoPST.state.isIdentifyEnabled) {
                window.ensureSidebarHidden();
            }
        }
    }
});

// 2. Long Press to Drop New Pin
let longPressTimer;
const LONG_PRESS_DURATION = 800; // ms
let isLongPressActive = false;
let recentlyDroppedPin = false; // Flag to prevent singleclick from closing sheet after long press
const viewport = map.getViewport();

function canDropPin() {
    return (
        !window.GeoPST.state.isMeasuring &&
        !window.GeoPST.state.isDrawing &&
        !window.GeoPST.state.isIdentifyEnabled
    );
}

viewport.addEventListener('touchstart', (e) => {
    if (!canDropPin() || e.touches.length > 1) return;
    isLongPressActive = true;
    const touch = e.touches[0];
    const pixel = [touch.clientX, touch.clientY];

    longPressTimer = setTimeout(() => {
        if (isLongPressActive) {
            handleLongPress(pixel);
        }
    }, LONG_PRESS_DURATION);
}, { passive: true });

viewport.addEventListener('touchend', () => {
    clearTimeout(longPressTimer);
    isLongPressActive = false;
});

viewport.addEventListener('touchmove', () => {
    clearTimeout(longPressTimer);
    isLongPressActive = false;
});

// 3. Right Click to Drop Pin (Desktop)
viewport.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    if (!canDropPin()) return;

    recentlyDroppedPin = true;
    const pixel = map.getEventPixel(e);
    const coordinate = map.getCoordinateFromPixel(pixel);
    if (coordinate) {
        placePin(coordinate);
    }

    setTimeout(() => { recentlyDroppedPin = false; }, 1000);
});

// --- PIN ACTIONS CONFIG ---
const PIN_ACTIONS = [
    { id: 'navigate', icon: 'fas fa-directions', label: 'الملاحة', class: 'primary' },
    { id: 'field-guide', icon: '', label: 'توجيه', class: 'secondary' },
    { id: 'save-loc', icon: 'far fa-bookmark', label: 'حفظ', class: 'secondary' },
    { id: 'share', icon: 'fas fa-share-alt', label: 'مشاركة', class: 'secondary' }
];

function setupNotePinActions(lat, lon, storageNoteKey) {
    window.featureSheet.onAction('navigate', () => {
        // استخدام أداة التوجيه الداخلية
        const projection = map.getView().getProjection();
        const destinationCoord = fromLonLat([parseFloat(lon), parseFloat(lat)], projection);
        window.featureSheet.close();
        if (typeof window.startRoutingTo === 'function') {
            window.startRoutingTo(destinationCoord);
        }
    });

    window.featureSheet.onAction('field-guide', () => {
        const projection = map.getView().getProjection();
        const targetCoord = fromLonLat([parseFloat(lon), parseFloat(lat)], projection);

        if (window.featureSheet) window.featureSheet.close();

        if (typeof window.startFieldGuidance === 'function') {
            window.startFieldGuidance(targetCoord);
        }
    });

    window.featureSheet.onAction('share', () => {
        const shareData = {
            title: 'موقع محدد - GeoPST',
            text: `موقع ${lat}, ${lon}`,
            // ✅ الرابط الرسمي والنظيف لمشاركة دبوس على خرائط جوجل
            url: `https://maps.google.com/?q=${lat},${lon}`
        };
        if (navigator.share) {
            navigator.share(shareData).catch(console.error);
        } else {
            navigator.clipboard.writeText(shareData.url).then(() => {
                showToast('تم نسخ الرابط للحافظة', 'success');
            }).catch(() => {
                alert(shareData.url);
            });
        }
    });

    window.featureSheet.onAction('save-loc', async () => {
        const isSaved = currentFeature.get('isSaved');
        const fetchFn = window.secureFetch || fetch;

        if (isSaved) {
            // Remove from server
            try {
                const response = await fetchFn('/api/users/pins', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ latitude: lat, longitude: lon })
                });

                if (response.ok) {
                    currentFeature.set('isSaved', false);
                    showToast('تمت إزالة الحفظ');
                }
            } catch (e) {
                showToast('فشل إلغاء الحفظ', 'error');
            }
        } else {
            // Add to server
            try {
                // Get current note from UI if any
                const currentNote = document.getElementById('pinNoteInput')?.value || '';
                
                const response = await fetchFn('/api/users/pins', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ latitude: lat, longitude: lon, note: currentNote })
                });

                if (response.ok) {
                    currentFeature.set('isSaved', true);
                    showToast('تم حفظ الموقع في حسابك', 'success');
                }
            } catch (e) {
                showToast('فشل حفظ الموقع', 'error');
            }
        }
        updateSaveButtonUI();
    });

    window.featureSheet.onAction('close', () => {
        if (currentFeature && !currentFeature.get('isSaved')) {
            pinSource.removeFeature(currentFeature);
            currentFeature = null;
        }

        // ✅ إرجاع حجم الخريطة عند إغلاق الدبوس من زر (X)
        if (window.ensureSidebarHidden && !window.GeoPST.state.isIdentifyEnabled) {
            window.ensureSidebarHidden();
        }
    });
}

function handleLongPress(pixel) {
    recentlyDroppedPin = true;
    const actualCoord = map.getCoordinateFromPixel(pixel);
    placePin(actualCoord);
    setTimeout(() => { recentlyDroppedPin = false; }, 1000);
}

// --- CORE LOGIC ---

function placePin(coordinate, existingFeature = null) {
    // Determine Feature
    if (existingFeature) {
        currentFeature = existingFeature;
    } else {
        // Dropping NEW Pin
        if (navigator.vibrate) navigator.vibrate(50);

        // Remove any other temporary pins
        const allFeatures = pinSource.getFeatures();
        allFeatures.forEach(f => {
            if (!f.get('isSaved')) {
                pinSource.removeFeature(f);
            }
        });

        // Add new temporary feature
        const feature = new Feature(new Point(coordinate));
        feature.set('isSaved', false);
        pinSource.addFeature(feature);
        currentFeature = feature;
    }

    // Animate Camera
    map.getView().animate({
        center: coordinate,
        zoom: Math.max(map.getView().getZoom() || 0, 11),
        duration: 400
    });

    // Calculate Lat/Lon for display/storage
    const projection = map.getView().getProjection();
    const lonLat = toLonLat(coordinate, projection);
    const lat = lonLat[1].toFixed(6);
    const lon = lonLat[0].toFixed(6);

    // Notes Key
    const storageNoteKey = `pin_${lat}_${lon}`;

    // --- SHOW SHEET ---
    if (window.featureSheet) {
        // ✅ إزاحة الخريطة عند فتح الدبوس
        if (window.ensureSidebarVisible) window.ensureSidebarVisible();

        window.featureSheet.clearCallbacks();
        setupNotePinActions(lat, lon, storageNoteKey);

        window.featureSheet.show({
            title: 'دبوس تم إسقاطه',
            subtitle: `${lat}, ${lon}`,
            keyMetric: {
                label: 'الإحداثيات',
                value: `${lat}, ${lon}`,
                icon: 'fas fa-map-marker-alt'
            },
            attributes: [],
            isPin: true,
            actions: PIN_ACTIONS
        });

        // Update Save Button Initial State
        updateSaveButtonUI();

        const detailsEl = document.querySelector('.bottom-sheet-details');
        if (detailsEl) {
            const savedNote = currentFeature.get('note') || '';

            // Fix XSS: Create structure first, then set value safely
            detailsEl.innerHTML = `
                    <div style="padding: 15px; direction: rtl;">
                        <h4 style="margin: 0 0 10px 0; color: var(--text-primary); font-size: 0.95rem;">
                            <i class="fas fa-sticky-note" style="color: var(--primary); margin-left: 8px;"></i>
                            ملاحظات على الموقع
                        </h4>
                         <textarea id="pinNoteInput" placeholder="أضف ملاحظة..." maxlength="500"
                            style="width: 100%; min-height: 80px; padding: 10px; border-radius: 8px; 
                            border: 1px solid #cbd5e1; background: var(--bg-secondary); color: var(--text-primary);
                            font-family: inherit; resize: vertical; margin-bottom: 5px;"></textarea>
                        <div id="noteCharCounter" style="font-size: 0.75rem; color: var(--text-muted); text-align: left; margin-bottom: 10px;">
                            0 / 500
                        </div>
                        <button id="saveNoteBtn" class="bottom-sheet-btn primary" style="width: 100%; justify-content: center;">
                            <i class="fas fa-save"></i> حفظ الملاحظة
                        </button>
                    </div>
                `;

             const noteTextarea = detailsEl.querySelector('#pinNoteInput');
             const counterEl = detailsEl.querySelector('#noteCharCounter');
             
             if (noteTextarea) {
                 noteTextarea.value = savedNote;
                 if (counterEl) counterEl.textContent = `${savedNote.length} / 500`;

                 noteTextarea.oninput = () => {
                     if (counterEl) counterEl.textContent = `${noteTextarea.value.length} / 500`;
                 };
             }

            // Logic for saving note to server
            const saveBtn = document.getElementById('saveNoteBtn');
            const textarea = document.getElementById('pinNoteInput');
            if (saveBtn) {
                saveBtn.onclick = async () => {
                    const fetchFn = window.secureFetch || fetch;
                    try {
                        const response = await fetchFn('/api/users/pins', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ latitude: lat, longitude: lon, note: textarea.value })
                        });
                        
                        if (response.ok) {
                            showToast('تم حفظ الملاحظة بنجاح', 'success');
                            currentFeature.set('isSaved', true); // Auto-save location if note is added
                            currentFeature.set('note', textarea.value);
                            updateSaveButtonUI();
                        }
                    } catch (e) {
                        showToast('فشل حفظ الملاحظة', 'error');
                    }
                };
            }
        }
    }
}

// Global Clear
window.clearDroppedPin = () => {
    pinSource.clear();
    setTimeout(loadSavedPins, 50);
};